import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, DollarSign, Settings, Info, CalendarRange } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { FormSection } from '@/components/common/FormSection';
import { useContractForm } from '../hooks';
import {
  BasicInfoSection,
  PricingSection,
  FinancialSection,
  AdditionalInfoSection,
  BillingPeriodsSection,
} from '../components';

export function ActionContractPage() {
  const { t } = useTranslation();
  const {
    form,
    onSubmit,
    navigate,
    isEditMode,
    isLoadingContract,
    isFormReady,
    isSubmitting,
    isFieldDisabled,
    showInstanceOption,
    selectedType,
    selectedCurrency,
    isBasicInfoComplete,
    isPricingComplete,
    isFinancialComplete,
    isBillingPeriodsComplete,
    clientIdFromUrl,
  } = useContractForm();

  if (isEditMode && (isLoadingContract || !isFormReady)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="max-w-4xl mx-auto p-6 space-y-4">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/app/contracts')}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">
                {isEditMode ? t('update') : t('new')} {t('contract')}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {t('requiredFieldsNote')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="max-w-4xl mx-auto p-6 space-y-4">
            {/* Section 1: Basic Information */}
            <FormSection
              title={t('basicInformation')}
              icon={FileText}
              isComplete={isBasicInfoComplete}
              defaultOpen={true}
            >
              <BasicInfoSection
                isFieldDisabled={isFieldDisabled}
                clientIdFromUrl={clientIdFromUrl}
              />
            </FormSection>

            {/* Section 2: Contract Type & Pricing */}
            {selectedType !== 'PRO_BONO' && (
              <FormSection
                title={t('contractTypePricing')}
                icon={DollarSign}
                isComplete={isPricingComplete}
                defaultOpen={true}
              >
                <PricingSection
                  isFieldDisabled={isFieldDisabled}
                  isEditMode={isEditMode}
                  selectedType={selectedType}
                  selectedCurrency={selectedCurrency}
                />
              </FormSection>
            )}

            {/* Section 3: Financial Details */}
            {selectedType !== 'PRO_BONO' && (
              <FormSection
                title={t('financialDetails')}
                icon={Settings}
                isComplete={isFinancialComplete}
                defaultOpen={true}
              >
                <FinancialSection
                  isFieldDisabled={isFieldDisabled}
                  selectedType={selectedType}
                  selectedCurrency={selectedCurrency}
                />
              </FormSection>
            )}

            {/* Section 4: Billing Periods (HOURLY only) */}
            {selectedType === 'HOURLY' && (
              <FormSection
                title={t('billingPeriods')}
                icon={CalendarRange}
                isComplete={isBillingPeriodsComplete}
                defaultOpen={true}
              >
                <BillingPeriodsSection
                  isFieldDisabled={false}
                  showInstanceOption={showInstanceOption}
                />
              </FormSection>
            )}

            {/* Section 5: Additional Information */}
            <FormSection
              title={t('additionalInformation')}
              icon={Info}
              defaultOpen={true}
            >
              <AdditionalInfoSection />
            </FormSection>
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 mt-6">
            <div className="max-w-4xl mx-auto px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/app/contracts')}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
